// src/components/navigation/SidebarSection.tsx
import { useState, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, type LucideIcon } from 'lucide-react';

interface SidebarSectionProps {
  title: string;
  icon: LucideIcon;
  storageKey: string;
  defaultOpen: boolean;
  children: ReactNode;
  /** hrefs of child items – used to auto-expand when a child route is active */
  childHrefs?: string[];
}

export default function SidebarSection({
  title,
  icon: Icon,
  storageKey,
  defaultOpen,
  children,
  childHrefs = [],
}: SidebarSectionProps) {
  const location = useLocation();
  const isChildActive = childHrefs.some((href) => location.pathname.startsWith(href));

  const [isOpen, setIsOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(`sidebar-${storageKey}`);
      if (stored !== null) return stored === 'true';
    } catch { /* localStorage unavailable */ }
    return defaultOpen;
  });

  // Auto-expand when a child route is active
  useEffect(() => {
    if (isChildActive && !isOpen) {
      setIsOpen(true);
    }
  }, [isChildActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(`sidebar-${storageKey}`, String(isOpen));
    } catch { /* localStorage unavailable */ }
  }, [isOpen, storageKey]);

  return (
    <div className="mt-3">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-muted-foreground transition-colors duration-150"
      >
        <span className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5" />
          {title}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${
            isOpen ? 'rotate-0' : '-rotate-90'
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="mt-1 space-y-0.5">{children}</div>
      </div>
    </div>
  );
}
